import { useState, useEffect } from 'react'
import {
  Button, Card, Input, Label, Textarea, Checkbox, Radio, Select, Modal, Alert, ProgressBar, Icon, Title, Text,
} from '@nimbus-ds/components'
import {
  ArrowLeftIcon, ArrowRightIcon, RocketIcon, ColorPaletteIcon, ClockIcon, ExternalLinkIcon,
} from '@nimbus-ds/icons'
import { useToast } from '@/hooks/use-toast'
import { Tier1VisualConfigSummary } from './configs/Tier1VisualConfigSummary'
import { MultiVariantSplit, type VariantEntry } from './MultiVariantSplit'
import { RiskLevelSelector } from './RiskLevelSelector'
import { useAuth } from '@/hooks/useAuth'
import { funnelService } from '@/api/funnel'
import { storeService } from '@/api/store'
import { apiConfig } from '@/config/api'
import type { ExperimentFormData, FunnelStep, RiskLevel } from '@/types'

interface ExperimentWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onExperimentCreate?: (experiment: ExperimentFormData) => void
  /** Store-level default risk level — pre-selects the control (RISK-LEVELS-COPY.md). */
  defaultRiskLevel?: RiskLevel
}

export function ExperimentWizard({
  open,
  onOpenChange,
  onExperimentCreate,
  defaultRiskLevel = 'balanced',
}: ExperimentWizardProps) {
  const { toast } = useToast()
  const { user } = useAuth()
  const [currentStep, setCurrentStep] = useState(1)
  const [availableFunnelSteps, setAvailableFunnelSteps] = useState<FunnelStep[]>([])
  const [formData, setFormData] = useState<ExperimentFormData>({
    name: '',
    objective: '',
    hypothesis: '',
    primarySegment: 'overall',
    type: 'tier1_visual', // Default to tier1_visual (unified Visual Editor)
    controlTraffic: 50,
    variantTraffic: 50,
    urlPatterns: '',
    devices: {
      desktop: true,
      mobile: true,
    },
    exclusions: '',
    funnelOption: 'default',
    funnelSteps: [],
    uploadSessionId: crypto.randomUUID(),
  })

  // Reset form when dialog is closed
  const handleDialogChange = (isOpen: boolean) => {
    if (!isOpen) {
      // Reset form when closing
      setCurrentStep(1)
      setFormData({
        name: '',
        objective: '',
        hypothesis: '',
        primarySegment: 'overall',
        type: 'tier1_visual', // Default to tier1_visual (unified Visual Editor)
        controlTraffic: 50,
        variantTraffic: 50,
        urlPatterns: '',
        devices: { desktop: true, mobile: true },
        exclusions: '',
        funnelOption: 'default',
        funnelSteps: [],
      })
    }
    onOpenChange(isOpen)
  }

  // Pre-select the store's default risk level when the dialog opens (the store default can
  // arrive async from ExperimentsPage; only apply while the user hasn't chosen one).
  useEffect(() => {
    if (!open) return
    setFormData(prev => (prev.riskLevel ? prev : { ...prev, riskLevel: defaultRiskLevel }))
  }, [open, defaultRiskLevel])

  // Fetch funnel steps when dialog opens
  useEffect(() => {
    if (!open || !user?.store_id) return
    funnelService.getFunnelConfig(user.store_id)
      .then(config => {
        setAvailableFunnelSteps(config.steps || [])
      })
      .catch(err => {
        console.warn('[Wizard] Failed to load funnel steps:', err.message)
      })
  }, [open, user?.store_id])

  // Listen for postMessage from visual editor
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Debug: Log all postMessages received
      console.log('[Wizard] postMessage received:', {
        type: event.data?.type,
        origin: event.origin,
        hasConfig: !!event.data?.config
      })

      // In production, validate event.origin for security
      // if (event.origin !== 'https://your-store-domain.com') return;

      // Handle legacy visual editor save (styles only)
      if (event.data?.type === 'TNX_VISUAL_EDITOR_SAVE') {
        console.log('[Wizard] ✅ Received visual editor config:', event.data.config)
        console.log('[Wizard]    Modifications:', event.data.config?.modifications)

        // Update form data with the visual editor config
        setFormData(prev => ({
          ...prev,
          variantConfig: event.data.config
        }))

        toast({
          title: 'Configuración Guardada',
          description: `${event.data.config?.modifications?.length || 0} modificación(es) recibida(s) del editor visual.`,
        })
      }
      // Handle unified visual editor save (styles + toggles + post_mutations)
      else if (event.data?.type === 'TNX_VISUAL_EDITOR_UNIFIED_SAVE') {
        console.log('[Wizard] ✅ Received unified visual editor config:', event.data.config)
        console.log('[Wizard]    Modifications:', event.data.config?.modifications?.length || 0)
        console.log('[Wizard]    Toggles:', event.data.config?.elements_to_hide?.length || 0)
        console.log('[Wizard]    Insertions:', event.data.config?.elements_to_insert?.length || 0)
        console.log('[Wizard]    Post-mutations:', event.data.config?.post_mutations?.length || 0)

        // Update form data with the unified config
        setFormData(prev => ({
          ...prev,
          variantConfig: event.data.config
        }))

        const cfg = event.data.config || {}
        const modCount = cfg.modifications?.length || 0
        const toggleCount = cfg.elements_to_hide?.length || 0
        const insertCount = cfg.elements_to_insert?.length || 0
        const postMutations: Array<{ type: string; transform?: string }> = cfg.post_mutations || []

        // Describe post_mutations by their actual operation, not as a bucket count.
        // text_transform with trim_cents_* is the Trim Cents template; reindex /
        // widget_update are housekeeping ops emitted by Gallery alongside its
        // toggles, so they don't deserve their own user-facing line.
        const trimCents = postMutations.find(m =>
          m.type === 'text_transform' &&
          (m.transform === 'trim_cents_all' || m.transform === 'trim_cents_zero')
        )

        const parts = []
        if (modCount > 0) parts.push(`${modCount} style mod(s)`)
        if (toggleCount > 0) parts.push(`${toggleCount} toggle(s)`)
        if (insertCount > 0) parts.push(`${insertCount} insertion(s)`)
        if (trimCents) parts.push('trim cents')
        const totalChanges = modCount + toggleCount + insertCount + (trimCents ? 1 : 0)

        toast({
          title: 'Changes Saved',
          description: totalChanges > 0
            ? `${parts.join(', ')} received (${totalChanges} total).`
            : 'No changes to save.',
        })
      } else {
        console.log('[Wizard] ⚠️ Received postMessage but type does not match:', event.data?.type)
      }
    }

    console.log('[Wizard] 🔵 Listening for postMessages from Visual Editor...')
    window.addEventListener('message', handleMessage)

    return () => {
      console.log('[Wizard] 🔴 Stopped listening for postMessages')
      window.removeEventListener('message', handleMessage)
    }
  }, [toast])

  const totalSteps = 4
  const progressPercentage = (currentStep / totalSteps) * 100

  const stepTitles = {
    1: 'Definir Experimento',
    2: 'Configuración Avanzada',
    3: 'Configuración de Variante',
    4: 'Revisión y Lanzamiento',
  }

  // experimentTypes removed - type selector hidden, defaults to tier1_visual

  const handleInputChange = (field: keyof ExperimentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleGoalTypeChange = (value: string) => {
    const goalType = value as 'purchase' | 'funnel_step' | 'custom_event'
    const labels: Record<string, string> = {
      purchase: 'Compra',
      funnel_step: 'Paso del funnel',
      custom_event: 'Evento personalizado'
    }
    handleInputChange('goals', {
      primary: {
        type: goalType,
        label: labels[goalType],
        step_id: goalType === 'funnel_step' ? (formData.goals?.primary?.step_id || '') : undefined,
        event_name: goalType === 'custom_event' ? (formData.goals?.primary?.event_name || '') : undefined
      },
      secondary: formData.goals?.secondary || []
    })
  }


  const handleDeviceChange = (
    device: keyof typeof formData.devices,
    checked: boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      devices: { ...prev.devices, [device]: checked },
    }))
  }

  // Device targeting helper functions
  const hasAnyDeviceSelected = () => {
    return formData.devices.desktop || formData.devices.mobile
  }

  const calculateTrafficPercentage = () => {
    // Approximate traffic share by device class (industry-typical for the
    // catalog vertical). Tablet was rolled into desktop at the 768px breakpoint.
    let total = 0
    if (formData.devices.mobile) total += 60
    if (formData.devices.desktop) total += 40
    return total
  }

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = () => {
    // Validate device targeting
    if (!hasAnyDeviceSelected()) {
      toast({
        title: 'Error de Validación',
        description: 'Debes seleccionar al menos un dispositivo para el experimento',
        variant: 'destructive'
      })
      return
    }

    if (onExperimentCreate) {
      // EXP_TIMING_001: Convert local datetime to UTC before submitting
      const submissionData: any = { ...formData }

      // Build timing object if any timing is enabled
      if (formData.enableStartTime || formData.enableEndTime) {
        submissionData.timing = {
          start_date_time: formData.enableStartTime && formData.startDateTime
            ? new Date(formData.startDateTime).toISOString()
            : undefined,
          end_date_time: formData.enableEndTime && formData.endDateTime
            ? new Date(formData.endDateTime).toISOString()
            : undefined,
        }
      }

      // Add session limit if enabled
      if (formData.enableSessionLimit && formData.sessionLimit) {
        submissionData.session_limit = formData.sessionLimit
      }

      onExperimentCreate(submissionData)
    }
    handleDialogChange(false) // Use the new handler to close and reset
  }

  // Visual Editor launcher
  const handleLaunchVisualEditor = async () => {
    // Not logged in or no current store — re-login is the right action.
    if (!user || !user.store_id) {
      toast({
        variant: 'destructive',
        title: 'Sesión inválida',
        description: 'No se pudo identificar tu tienda actual. Por favor, vuelve a iniciar sesión.',
      })
      return
    }

    // Get store URL. Legacy TN OAuth JWTs carry it in user_data; Google SSO JWTs
    // don't, so we fetch from the store API as a fallback.
    let storeUrl: string | undefined = user.store_url || user.original_domain
    if (!storeUrl) {
      try {
        const storeInfo = await storeService.getStore(user.store_id)
        storeUrl = storeInfo?.domain
      } catch (err) {
        console.error('Failed to fetch store info for visual editor:', err)
      }
    }

    if (!storeUrl) {
      toast({
        variant: 'destructive',
        title: 'Tienda sin dominio',
        description: `La tienda ${user.store_id} no tiene un dominio configurado. El editor visual no puede abrirse hasta que la tienda esté completamente conectada vía TiendaNube.`,
      })
      return
    }

    // Normalize URL: remove any existing protocol and add https://
    // This handles cases where TiendaNube API returns domains with http:// prefix
    storeUrl = storeUrl.replace(/^https?:\/\//, '')
    storeUrl = `https://${storeUrl}`

    // Add tnx_visual_editor parameter to activate editor mode via tnx-client.js
    // Pass variant_count and upload_session_id for template mode
    const variantCount = formData.variantsList ? Object.keys(formData.variantsList).length : 2
    const separator = storeUrl.includes('?') ? '&' : '?'
    const tnxApiUrl = encodeURIComponent(apiConfig.baseURL)
    const tnxStoreId = encodeURIComponent(user?.store_id || '')
    // Device targeting passes through as a CSV. The editor reads this and
    // locks its device toggle to the listed values — consultants can't pick
    // selectors for a device the experiment doesn't target.
    const allowedDevices = (['desktop', 'mobile'] as const).filter(d => formData.devices[d]).join(',')
    const devicesParam = allowedDevices ? `&devices=${allowedDevices}` : ''
    const editorUrl = `${storeUrl}${separator}tnx_visual_editor=true&variant_count=${variantCount}&upload_session_id=${formData.uploadSessionId || ''}&tnx_api_url=${tnxApiUrl}&tnx_store_id=${tnxStoreId}${devicesParam}`

    // Open the editor window sized to the targeted device. For mobile-only
    // experiments, opening at mobile width makes the storefront's own media
    // queries fire mobile — the consultant sees the actual mobile layout, not
    // a desktop layout claiming to be in mobile mode. Default (both / desktop)
    // stays at 1400x900.
    const mobileOnly = formData.devices.mobile && !formData.devices.desktop
    const editorWindowFeatures = mobileOnly
      ? 'width=420,height=900'
      : 'width=1400,height=900'
    const editorWindow = window.open(editorUrl, 'VisualEditor', editorWindowFeatures)

    if (editorWindow) {
      toast({
        title: 'Editor Visual Abierto',
        description: 'El editor visual se está cargando en una nueva ventana.',
      })
    } else {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudo abrir el editor visual. Verifica que los pop-ups estén habilitados.',
      })
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre del Experimento</Label>
              <Input
                id="name"
                placeholder="ej. Botón CTA Rojo vs Azul"
                value={formData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('name', e.target.value)
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="objective">Objetivo</Label>
              <Input
                id="objective"
                placeholder="ej. Aumentar clicks en botón de compra"
                value={formData.objective}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('objective', e.target.value)
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hypothesis">Hipótesis</Label>
              <Textarea
                id="hypothesis"
                placeholder="ej. Un botón rojo será más llamativo y generará más clicks que el azul actual"
                value={formData.hypothesis}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handleInputChange('hypothesis', e.target.value)
                }
                lines={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mde">
                Efecto mínimo detectable (MDE){' '}
                <span className="text-muted-foreground font-normal">— opcional</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="mde"
                  type="number"
                  min={1}
                  max={100}
                  step={1}
                  className="w-28"
                  placeholder="10"
                  value={formData.mde ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('mde', e.target.value === '' ? null : Number(e.target.value))
                  }
                />
                <Text as="span">% de cambio relativo</Text>
              </div>
              <Text fontSize="caption">
                El cambio mínimo que te importaría detectar. Define la banda de indiferencia: efectos
                más chicos se reportan como "sin diferencia real" en vez de falsos positivos con poca
                muestra. Por defecto ±10%.
              </Text>
            </div>

            <div className="space-y-2">
              <Label htmlFor="primary-segment">Segmento principal</Label>
              <Select
                id="primary-segment"
                name="primary-segment"
                value={formData.primarySegment || 'overall'}
                onChange={(e) => handleInputChange('primarySegment', e.target.value)}
              >
                <Select.Option value="overall" label="General (todos los dispositivos)" />
                <Select.Option value="mobile" label="Mobile" />
                <Select.Option value="desktop" label="Desktop" />
              </Select>
              <Text fontSize="caption">
                El segmento sobre el que se decide implementar o no. Los resultados por dispositivo
                de los demás segmentos se muestran como exploratorios (sin decidir sobre ellos).
                Elegí "General" salvo que la apuesta sea específica de un dispositivo.
              </Text>
            </div>

            <div className="flex items-start gap-3 rounded-md border bg-muted/30 p-3">
              <Checkbox
                name="hard-to-revert"
                id="hard-to-revert"
                checked={formData.hardToRevert === true}
                onChange={(e) =>
                  handleInputChange('hardToRevert', e.target.checked === true)
                }
              />
              <div className="space-y-1">
                <Label htmlFor="hard-to-revert" className="cursor-pointer font-medium">
                  Cambio difícil de revertir
                </Label>
                <Text fontSize="caption">
                  Marcalo si deshacer este cambio cuesta (rediseños grandes, cambios de precios,
                  estructura del catálogo). Deshabilita el modo Ágil y recomienda esperar un
                  resultado sólido antes de aplicar.
                </Text>
              </div>
            </div>

            <RiskLevelSelector
              value={formData.riskLevel || defaultRiskLevel}
              onChange={(level) => handleInputChange('riskLevel', level)}
              recommended={formData.hardToRevert ? 'safe' : defaultRiskLevel}
              hardToRevert={formData.hardToRevert === true}
              name="wizard-risk-level"
            />

            <div className="space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <Text fontSize="caption" as="span" className="bg-background px-2">
                    o configura manualmente
                  </Text>
                </div>
              </div>
            </div>

            {/* Type selector hidden - defaults to tier1_visual */}

            <div className="flex items-start gap-3 rounded-md border bg-muted/30 p-3">
              <Checkbox
                name="is-negative"
                id="is-negative"
                checked={formData.isNegative === true}
                onChange={(e) =>
                  handleInputChange('isNegative', e.target.checked === true)
                }
              />
              <div className="space-y-1">
                <Label htmlFor="is-negative" className="cursor-pointer font-medium">
                  Test negativo
                </Label>
                <Text fontSize="caption">
                  Invierte control y tratamiento en los cálculos. Úsalo cuando el cambio bajo prueba ya está en producción y el experimento lo oculta (la "variante" es la versión sin el cambio).
                </Text>
              </div>
            </div>

            <div className="space-y-3">
              <Label>Métrica de Éxito</Label>
              <div className="space-y-2">
                <Radio
                  name="goal-type"
                  id="goal-purchase"
                  value="purchase"
                  label="Compra (por defecto)"
                  checked={(formData.goals?.primary?.type || 'purchase') === 'purchase'}
                  onChange={(e) => handleGoalTypeChange(e.target.value)}
                />
                <Radio
                  name="goal-type"
                  id="goal-funnel"
                  value="funnel_step"
                  label="Paso del funnel"
                  checked={formData.goals?.primary?.type === 'funnel_step'}
                  onChange={(e) => handleGoalTypeChange(e.target.value)}
                />
                <Radio
                  name="goal-type"
                  id="goal-event"
                  value="custom_event"
                  label="Evento personalizado"
                  checked={formData.goals?.primary?.type === 'custom_event'}
                  onChange={(e) => handleGoalTypeChange(e.target.value)}
                />
              </div>

              {formData.goals?.primary?.type === 'funnel_step' && (
                availableFunnelSteps.length > 0 ? (
                  <Select
                    id="goal-funnel-step"
                    name="goal-funnel-step"
                    value={formData.goals.primary.step_id || ''}
                    onChange={(e) => {
                      const value = e.target.value
                      const step = availableFunnelSteps.find(s => s.id === value)
                      handleInputChange('goals', {
                        ...formData.goals!,
                        primary: { ...formData.goals!.primary, step_id: value, label: step ? `Paso: ${step.name}` : `Paso: ${value}` }
                      })
                    }}
                  >
                    <Select.Option value="" label="Seleccionar paso del funnel" />
                    {availableFunnelSteps
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                      .map(step => (
                        <Select.Option key={step.id} value={step.id} label={`${step.name} (${step.id})`} />
                      ))}
                  </Select>
                ) : (
                  <Text>Cargando pasos del funnel...</Text>
                )
              )}

              {formData.goals?.primary?.type === 'custom_event' && (
                <Input
                  placeholder="Nombre del evento (ej: item_size_click)"
                  value={formData.goals.primary.event_name || ''}
                  onChange={(e) => handleInputChange('goals', {
                    ...formData.goals!,
                    primary: { ...formData.goals!.primary, event_name: e.target.value, label: `Evento: ${e.target.value}` }
                  })}
                />
              )}

              <Text>
                Define la conversión que el test estadístico va a medir. Las compras siempre se rastrean en paralelo.
              </Text>
            </div>
          </div>
        )

      case 2:
        return (
          <div className="space-y-6">
            {/* Variants & Traffic Configuration */}
            <MultiVariantSplit
              variants={formData.variantsList || {
                control: { name: 'Control', trafficPct: formData.controlTraffic },
                treatment: { name: 'Variante B', trafficPct: formData.variantTraffic },
              }}
              onChange={(variants: Record<string, VariantEntry>) => {
                handleInputChange('variantsList', variants)
                // Keep legacy fields in sync for backward compat
                const control = variants.control?.trafficPct ?? 50
                handleInputChange('controlTraffic', control)
                handleInputChange('variantTraffic', 100 - control)
              }}
            />

            {/* Session Limit */}
            <div className="space-y-2 mt-4 pt-4 border-t">
              <div className="flex items-center space-x-2">
                <Checkbox
                  name="enable-session-limit"
                  id="enable-session-limit"
                  label="Limitar a un número máximo de sesiones"
                  checked={formData.enableSessionLimit || false}
                  onChange={(e) =>
                    handleInputChange('enableSessionLimit', e.target.checked as boolean)
                  }
                />
              </div>
              {formData.enableSessionLimit && (
                <div className="ml-6 space-y-2">
                  <Input
                    type="number"
                    min="100"
                    step="100"
                    placeholder="Ej: 5000 sesiones"
                    value={formData.sessionLimit || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('sessionLimit', parseInt(e.target.value) || 0)
                    }
                  />
                  <Text fontSize="caption">
                    Útil para pruebas presupuestadas o tests de baja escala
                  </Text>
                </div>
              )}
            </div>

            {/* Targeting */}
            <div className="space-y-2">
              <Label htmlFor="url-patterns">Targeting de Páginas</Label>
              <Input
                id="url-patterns"
                placeholder="/products/*, /collections/sale*"
                value={formData.urlPatterns}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('urlPatterns', e.target.value)
                }
              />
              <Text>
                Define dónde se mostrará el experimento (usa * para comodines)
              </Text>
            </div>

            {/* Device Conditions */}
            <div className="space-y-4">
              <Label>Condiciones del Dispositivo</Label>
              <div className="flex gap-6">
                {['desktop', 'mobile'].map((device) => (
                  <div key={device} className="flex items-center space-x-2">
                    <Checkbox
                      name={device}
                      id={device}
                      label={device}
                      checked={
                        formData.devices[
                          device as keyof typeof formData.devices
                        ]
                      }
                      onChange={(e) =>
                        handleDeviceChange(
                          device as keyof typeof formData.devices,
                          e.target.checked as boolean
                        )
                      }
                    />
                  </div>
                ))}
              </div>
              <Text>
                Selecciona en qué dispositivos mostrar el experimento
              </Text>
              <Text fontSize="caption">
                Tráfico estimado: ~{calculateTrafficPercentage()}% de visitantes
              </Text>

              {!hasAnyDeviceSelected() && (
                <Alert appearance="danger">
                  Debes seleccionar al menos un dispositivo
                </Alert>
              )}
            </div>

            {/* Exclusions */}
            <div className="space-y-2">
              <Label htmlFor="exclusions">Exclusiones</Label>
              <Textarea
                id="exclusions"
                placeholder="user_id:admin*, ip:192.168.1.*, country:US"
                value={formData.exclusions}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handleInputChange('exclusions', e.target.value)
                }
                lines={3}
              />
              <Text>
                Define usuarios o condiciones que deben ser excluidos del
                experimento
              </Text>
            </div>

            {/* Funnel Configuration */}
            <div className="space-y-4">
              <Label>Configuración del Funnel</Label>
              <div className="space-y-2">
                <Radio
                  name="funnel-option"
                  id="funnel-default"
                  value="default"
                  label="Usar funnel por defecto de la tienda"
                  checked={formData.funnelOption === 'default'}
                  onChange={(e) => handleInputChange('funnelOption', e.target.value)}
                />
                <Radio
                  name="funnel-option"
                  id="funnel-custom"
                  value="custom"
                  label="Personalizar pasos del funnel para este experimento"
                  checked={formData.funnelOption === 'custom'}
                  onChange={(e) => handleInputChange('funnelOption', e.target.value)}
                />
              </div>

              {formData.funnelOption === 'custom' && (
                <Card>
                  <Card.Header>
                    <Title as="h4">
                      Seleccionar Pasos del Funnel
                    </Title>
                  </Card.Header>
                  <Card.Body>
                    <Text>
                      La configuración personalizada del funnel estará
                      disponible próximamente.
                    </Text>
                  </Card.Body>
                </Card>
              )}
            </div>

            {/* EXP_TIMING_001: Experiment Scheduling */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="mb-3 space-y-0.5">
                <div className="flex items-center gap-2">
                  <Icon source={<ClockIcon />} />
                  <Text fontWeight="bold" color="neutral-textHigh">Programación del Experimento</Text>
                </div>
                <Text fontSize="caption">
                  Define cuándo iniciará y finalizará tu experimento (opcional)
                </Text>
              </div>
               <div className="space-y-4">
                {/* Start Time */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      name="enable-start-time"
                      id="enable-start-time"
                      label="Programar hora de inicio"
                      checked={formData.enableStartTime || false}
                      onChange={(e) => {
                        const checked = e.target.checked
                        handleInputChange('enableStartTime', checked as boolean)
                        // Set default start time (tomorrow at 00:00) when enabled
                        if (checked && !formData.startDateTime) {
                          const tomorrow = new Date()
                          tomorrow.setDate(tomorrow.getDate() + 1)
                          tomorrow.setHours(0, 0, 0, 0)
                          const localDateTime = tomorrow.toISOString().slice(0, 16)
                          handleInputChange('startDateTime', localDateTime)
                        }
                      }}
                    />
                  </div>
                  {formData.enableStartTime && (
                    <div className="ml-6 space-y-2">
                      <Input
                        type="datetime-local"
                        value={formData.startDateTime || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleInputChange('startDateTime', e.target.value)
                        }
                      />
                      <Text fontSize="caption">
                        Hora local: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                      </Text>
                    </div>
                  )}
                </div>

                {/* End Time */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      name="enable-end-time"
                      id="enable-end-time"
                      label="Programar hora de finalización"
                      checked={formData.enableEndTime || false}
                      onChange={(e) => {
                        const checked = e.target.checked
                        handleInputChange('enableEndTime', checked as boolean)
                        // Set default end time (7 days from now at 23:59) when enabled
                        if (checked && !formData.endDateTime) {
                          const weekFromNow = new Date()
                          weekFromNow.setDate(weekFromNow.getDate() + 7)
                          weekFromNow.setHours(23, 59, 0, 0)
                          const localDateTime = weekFromNow.toISOString().slice(0, 16)
                          handleInputChange('endDateTime', localDateTime)
                        }
                      }}
                    />
                  </div>
                  {formData.enableEndTime && (
                    <div className="ml-6 space-y-2">
                      <Input
                        type="datetime-local"
                        value={formData.endDateTime || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleInputChange('endDateTime', e.target.value)
                        }
                      />
                      <Text fontSize="caption">
                        Hora local: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                      </Text>
                    </div>
                  )}
                </div>
               </div>
            </div>
          </div>
        )

      case 3:
        return (
          <div className="space-y-6">
            {/* VISUAL EDITOR - Professional tool card design */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="mb-3 space-y-0.5">
                <div className="flex items-center gap-2">
                  <Icon source={<ColorPaletteIcon />} />
                  <Text fontWeight="bold" color="neutral-textHigh">Editor Visual</Text>
                </div>
                <Text fontSize="caption">
                  Modifica elementos de tu tienda visualmente. Abre en nueva ventana para editar colores, textos y estilos.
                </Text>
              </div>
               <div className="space-y-4">
                <div className="space-y-3">
                  <Text>
                    Selecciona elementos directamente en tu tienda y aplica cambios visuales sin código. Los cambios se guardan automáticamente al hacer clic en "Guardar" en el editor.
                  </Text>
                  <Button onClick={handleLaunchVisualEditor} appearance="primary">
                    <Icon source={<ExternalLinkIcon />} color="currentColor" />
                    Abrir Editor Visual
                  </Button>
                </div>

                {/* Subtle helper text */}
                <div className="space-y-1 pt-3 border-t">
                  <Text fontSize="caption" className="flex items-center gap-1">
                    <span className="text-muted-foreground/60">💡</span> Haz clic en cualquier elemento para seleccionarlo
                  </Text>
                  <Text fontSize="caption" className="flex items-center gap-1">
                    <span className="text-muted-foreground/60">💡</span> Compatible con experimentos multi-página
                  </Text>
                </div>
               </div>
            </div>

            {/* Show read-only summary of Visual Editor changes */}
            {formData.variantConfig?.type === 'tier1_visual' && (
              <Tier1VisualConfigSummary config={formData.variantConfig} />
            )}
          </div>
        )

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--nimbus-colors-primary-surface)]">
                <Icon source={<RocketIcon />} />
              </div>
              <Title as="h3" className="mb-2">
                Experimento Listo para Lanzar
              </Title>
              <Text>
                Revisa la configuración final y lanza tu experimento
              </Text>
            </div>

            <Card>
              <Card.Header>
                <Title as="h4">
                  Resumen del Experimento
                </Title>
              </Card.Header>
              <Card.Body>
               <div className="space-y-3">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">
                    NOMBRE
                  </Label>
                  <Text>{formData.name || 'Sin nombre'}</Text>
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">
                    OBJETIVO
                  </Label>
                  <Text>
                    {formData.objective || 'Sin objetivo definido'}
                  </Text>
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">
                    TIPO
                  </Label>
                  <Text>
                    {formData.type || 'Sin tipo seleccionado'}
                  </Text>
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">
                    DISTRIBUCIÓN
                  </Label>
                  <Text>
                    {formData.variantsList
                      ? Object.entries(formData.variantsList).map(([_k, v]) => `${v.name}: ${v.trafficPct}%`).join(' | ')
                      : `Control: ${formData.controlTraffic}% | Variante: ${formData.variantTraffic}%`
                    }
                  </Text>
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">
                    MÉTRICA DE ÉXITO
                  </Label>
                  <Text>
                    {formData.goals?.primary?.label || 'Compra'}
                  </Text>
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">
                    DISPOSITIVOS
                  </Label>
                  <Text>
                    {[
                      formData.devices.desktop ? 'Desktop' : null,
                      formData.devices.mobile ? 'Mobile' : null,
                    ].filter(Boolean).join(' + ') || '⚠️ Ninguno seleccionado'}
                    {(formData.devices.desktop && formData.devices.mobile) && (
                      <span className="text-muted-foreground"> (todos)</span>
                    )}
                  </Text>
                </div>
                {formData.urlPatterns && (
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">
                      URL
                    </Label>
                    <Text>{formData.urlPatterns}</Text>
                  </div>
                )}
                {formData.variantConfig && (
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">
                      CONFIGURACIÓN DE VARIANTE
                    </Label>
                    {formData.variantConfig.type === 'tier1_visual' && (() => {
                      const cfg = formData.variantConfig
                      // Aggregate per-element device tags so the consultant can
                      // verify which device each change applies to. 'all' or
                      // missing is shown as "todos".
                      const byDevice = (items: Array<{ device?: string }> = []) => {
                        const counts: Record<string, number> = {}
                        for (const it of items) {
                          const d = it.device || 'all'
                          counts[d] = (counts[d] || 0) + 1
                        }
                        return counts
                      }
                      const fmt = (label: string, items: Array<{ device?: string }> = []) => {
                        if (!items.length) return null
                        const counts = byDevice(items)
                        const breakdown = Object.entries(counts)
                          .map(([d, n]) => `${n} ${d === 'all' ? 'todos' : d}`)
                          .join(', ')
                        return `${items.length} ${label} (${breakdown})`
                      }
                      const parts = [
                        fmt('estilo(s)', cfg.modifications),
                        fmt('toggle(s)', cfg.elements_to_hide),
                        fmt('inserción(es)', cfg.elements_to_insert),
                        // In-place swaps don't carry per-element device — their
                        // srcset combines mobile + desktop URLs at runtime.
                        cfg.elements_to_swap?.length ? `${cfg.elements_to_swap.length} image swap(s)` : null,
                      ].filter(Boolean)
                      return (
                        <Text>
                          {parts.length ? parts.join(' | ') : '⚠️ Sin cambios'}
                        </Text>
                      )
                    })()}
                  </div>
                )}

               </div>
              </Card.Body>
            </Card>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <Modal open={open} onDismiss={() => handleDialogChange(false)}>
      <Modal.Header title="Crear Nuevo Experimento" />
      <Modal.Body>
        <Text>
          Configura y lanza un nuevo experimento A/B para optimizar tu tienda
        </Text>

        {/* Progress */}
        <div className="space-y-2 mt-3">
          <div className="flex justify-between text-sm">
            <Text as="span" color="neutral-textHigh" fontWeight="medium">
              {stepTitles[currentStep as keyof typeof stepTitles]}
            </Text>
            <Text as="span">
              Paso {currentStep} de {totalSteps}
            </Text>
          </div>
          <ProgressBar value={progressPercentage} appearance="primary" />
        </div>

        {/* Wizard Steps */}
        <div className="flex justify-center space-x-4 py-4">
          {[1, 2, 3, 4].map((step) => (
            <div
              key={step}
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                step === currentStep
                  ? 'bg-[var(--nimbus-colors-primary-interactive)] text-white'
                  : step < currentStep
                    ? 'bg-[var(--nimbus-colors-primary-surface)] text-[var(--nimbus-colors-primary-textHigh)]'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              {step}
            </div>
          ))}
        </div>

        {/* Step Content — cap height so the modal never outgrows the viewport; form scrolls, header/footer stay put */}
        <div className="px-1 py-4 max-h-[55vh] overflow-y-auto" style={{ scrollbarGutter: 'stable both-edges' }}>{renderStep()}</div>
      </Modal.Body>

      {/* Navigation */}
      <Modal.Footer>
        <Button
          type="button"
          appearance="neutral"
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          <Icon source={<ArrowLeftIcon />} color="currentColor" />
          Anterior
        </Button>

        {currentStep < totalSteps ? (
          <Button type="button" appearance="primary" onClick={nextStep}>
            Siguiente
            <Icon source={<ArrowRightIcon />} color="currentColor" />
          </Button>
        ) : (
          <Button type="button" appearance="primary" onClick={handleSubmit}>
            Lanzar Experimento
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
